import { useState, useCallback, useRef, useEffect } from 'react';

export interface UseBulkSelectionReturn<T extends { id: string }> {
  selectedIds: Set<string>;
  setSelectedIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  toggleSelect: (id: string, shiftKey?: boolean) => void;
  toggleSelectAll: () => void;
  clearSelection: () => void;
  isSelected: (id: string) => boolean;
  isAllSelected: boolean;
  isPartiallySelected: boolean;
  selectedCount: number;
  selectedItems: T[];
  masterCheckboxRef: React.RefObject<HTMLInputElement | null>;
}

/**
 * Hook to manage Excel-like row selection with Shift + Click range selection,
 * Select-All, and tri-state (indeterminate) checkbox support.
 */
export function useBulkSelection<T extends { id: string }>(items: T[]): UseBulkSelectionReturn<T> {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [lastSelectedId, setLastSelectedId] = useState<string | null>(null);
  const masterCheckboxRef = useRef<HTMLInputElement | null>(null);

  // Toggle single item or Shift+Click range
  const toggleSelect = useCallback(
    (targetId: string, shiftKey = false) => {
      if (shiftKey && lastSelectedId && lastSelectedId !== targetId) {
        const lastIdx = items.findIndex((i) => i.id === lastSelectedId);
        const currIdx = items.findIndex((i) => i.id === targetId);

        if (lastIdx !== -1 && currIdx !== -1) {
          const start = Math.min(lastIdx, currIdx);
          const end = Math.max(lastIdx, currIdx);
          const rangeSlice = items.slice(start, end + 1);

          setSelectedIds((prev) => {
            const next = new Set(prev);
            // If the target item is currently selected, range-unselect, else range-select
            const shouldSelect = !prev.has(targetId);
            for (const item of rangeSlice) {
              if (shouldSelect) {
                next.add(item.id);
              } else {
                next.delete(item.id);
              }
            }
            return next;
          });
          setLastSelectedId(targetId);
          return;
        }
      }

      // Standard toggle
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(targetId)) {
          next.delete(targetId);
        } else {
          next.add(targetId);
        }
        return next;
      });
      setLastSelectedId(targetId);
    },
    [items, lastSelectedId]
  );

  const isAllSelected = items.length > 0 && items.every((i) => selectedIds.has(i.id));
  const isPartiallySelected = selectedIds.size > 0 && !isAllSelected;

  // Toggle select all visible items
  const toggleSelectAll = useCallback(() => {
    if (isAllSelected) {
      setSelectedIds(new Set());
      setLastSelectedId(null);
    } else {
      setSelectedIds(new Set(items.map((i) => i.id)));
    }
  }, [items, isAllSelected]);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setLastSelectedId(null);
  }, []);

  const isSelected = useCallback(
    (id: string) => selectedIds.has(id),
    [selectedIds]
  );

  // Sync indeterminate state of master checkbox
  useEffect(() => {
    if (masterCheckboxRef.current) {
      masterCheckboxRef.current.indeterminate = isPartiallySelected;
    }
  }, [isPartiallySelected]);

  const selectedItems = items.filter((i) => selectedIds.has(i.id));

  return {
    selectedIds,
    setSelectedIds,
    toggleSelect,
    toggleSelectAll,
    clearSelection,
    isSelected,
    isAllSelected,
    isPartiallySelected,
    selectedCount: selectedIds.size,
    selectedItems,
    masterCheckboxRef,
  };
}

